import { ReactNode, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  MRT_RowData,
  MRT_SortingState,
  MRT_TableOptions,
  MRT_TableContainer as Table,
  useMaterialReactTable,
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableInstance,
  MRT_Cell,
} from 'material-react-table';

// MUI
import { Typography, useTheme, useMediaQuery, Box, BoxProps, Theme } from '@mui/material';

// common
import NewPagination, { type NewPaginationProps } from 'common/components/NewPagination';
import { SHELL } from 'config/setupTheme';

const MIN_COLUMN_WIDTH = 112;

/** Cột đều nhau, chỉ kẻ dọc giữa các cột. */
const evenColumnSx = (theme: Theme) => ({
  '& .MuiTable-root, & .MuiTableHead-root, & .MuiTableBody-root': {
    minWidth: 'max-content',
  },
  '& .MuiTableRow-head, & .MuiTableBody-root .MuiTableRow-root': {
    display: 'flex',
    width: 'max-content',
    minWidth: '100%',
  },
  // Màn hẹp: cột không co dưới mức này, bảng cuộn ngang thay vì ngắt chữ từng ký tự.
  '& .MuiTableCell-head, & .MuiTableCell-body': {
    flex: '1 1 0px !important',
    width: '0 !important',
    minWidth: `${MIN_COLUMN_WIDTH}px !important`,
    maxWidth: 'none !important',
    borderLeft: 'none',
    borderRight: 'none',
  },
  '& .MuiTableCell-head:not(:last-of-type), & .MuiTableCell-body:not(:last-of-type)': {
    borderRight: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(244, 246, 252, 0.12)' : SHELL.line}`,
  },
  '& .MuiTableCell-root[data-pinned="true"]': {
    opacity: '1 !important',
    borderLeft: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(244, 246, 252, 0.12)' : SHELL.line}`,
  },
  '& .MuiTableCell-head': {
    alignItems: 'center',
    minHeight: 72,
    height: 'auto',
    overflow: 'visible',
    paddingTop: '14px !important',
    paddingBottom: '14px !important',
  },
  '& .Mui-TableHeadCell-Content, & .Mui-TableHeadCell-Content-Labels, & .Mui-TableHeadCell-Content-Wrapper': {
    overflow: 'visible',
    height: 'auto',
    whiteSpace: 'pre-wrap',
    lineHeight: 1.45,
  },
});

interface Props<T extends MRT_RowData = MRT_RowData> {
  paginationProps: NewPaginationProps;
  onSortingChange?: (value: MRT_SortingState) => void;
  renderRowActions?: (props: {
    isTablet: boolean;
    closeMenu?: () => void;
    row: MRT_Row<T>;
    cell?: MRT_Cell<T>;
    staticRowIndex?: number;
    table: MRT_TableInstance<T>;
  }) => ReactNode[];
  boxProps?: BoxProps;
  customFilterUi: ReactNode;
}

const NewMuiTable = <T extends MRT_RowData = MRT_RowData>(
  props: Props<T> & Omit<MRT_TableOptions<T>, 'onSortingChange' | 'renderRowActions'>
) => {
  const {
    boxProps,
    paginationProps,
    state,
    muiSearchTextFieldProps,
    muiTablePaperProps,
    muiTopToolbarProps,
    onSortingChange,
    muiTableBodyCellProps,
    muiTableHeadCellProps,
    enableRowVirtualization,
    renderBottomToolbarCustomActions,
    columns,
    renderRowActions,
    customFilterUi,
    ...rest
  } = props;

  const [sorting, setSort] = useState<MRT_SortingState>(state?.sorting || []);

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('laptop'));
  const headerBg = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : theme.palette.grey[100];
  const intl = useIntl();

  const mappedColumns = useMemo<MRT_ColumnDef<T, any>[]>(() => {
    return [...columns].map((column) => ({
      enableSorting: false,
      ...column,
      size: 160,
      minSize: 30,
      grow: true,
    }));
  }, [columns]);

  const renderActions = (params) => {
    const actions = renderRowActions!({ isTablet, ...params });
    return actions.map((item, index) => <Box key={index}>{item}</Box>);
  };

  const table = useMaterialReactTable<T>({
    columns: mappedColumns,
    layoutMode: 'grid',
    enableColumnPinning: true,
    enableStickyHeader: true,
    enableFullScreenToggle: false,
    enableTopToolbar: false,
    columnFilterDisplayMode: 'custom',
    enableColumnActions: false,
    enableFilterMatchHighlighting: false,
    positionActionsColumn: 'last',
    paginationDisplayMode: 'pages',
    muiPaginationProps: {
      color: 'primary',
      shape: 'rounded',
      variant: 'outlined',
    },
    muiTableContainerProps: { sx: { maxHeight: '70dvh' } },
    initialState: { columnPinning: { right: ['mrt-row-actions'] } },
    localization: {
      rowsPerPage: intl.formatMessage({ id: 'rowsPerPage' }),
    },
    state: {
      density: 'compact',
      sorting,
      isLoading: false,
      ...state,
    },
    muiTableProps: {
      sx: {
        '& .Mui-TableBodyCell-DetailPanel': {
          border: 'none',
        },
        '& .MuiTableRow-footer': {
          bgcolor: 'grey.50',
        },
        '& .MuiTableCell-root[data-pinned="true"]:before': {
          backgroundColor: 'background.paper',
        },
        '& .MuiTableCell-head[data-pinned="true"]:before': {
          backgroundColor: headerBg,
        },
      },
    },
    muiTableBodyCellProps: {
      align: 'center',
      sx: {
        minWidth: 'unset',
        whiteSpace: 'pre-wrap',
        lineHeight: 1.45,
        wordBreak: 'break-word',
      },
      size: 'small',
    },
    displayColumnDefOptions: {
      'mrt-row-expand': {
        size: 10,
        muiTableHeadCellProps: {
          sx: { maxWidth: 40 },
        },
        muiTableBodyCellProps: {
          sx: { maxWidth: 40 },
        },
      },
      'mrt-row-actions': {
        header: 'action',
        Header: isTablet ? <Box /> : <FormattedMessage id="action" />,
        size: 160,
        minSize: 30,
        grow: true,
      },
    },
    renderRowActions: isTablet
      ? undefined
      : (params) => (renderRowActions ? renderActions(params) : []),
    renderRowActionMenuItems: (params) => {
      return isTablet && renderRowActions ? renderActions(params) : [];
    },
    getRowId: ({ id }) => id,
    renderEmptyRowsFallback: () => (
      <Typography
        variant="body1"
        color="textSecondary"
        sx={(theme) => ({
          margin: 0,
          fontStyle: 'italic',
          height: 120,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          [theme.breakpoints.up('tablet')]: {
            height: 240,
          },
        })}
        component={'div'}
      >
        <FormattedMessage id="emptyData" />
      </Typography>
    ),
    muiDetailPanelProps: () => ({ sx: () => ({ p: 0 }) }),
    muiTableHeadCellProps: () => ({
      align: 'center',
      sx: {
        bgcolor: headerBg,
        whiteSpace: 'pre-wrap',
        '&:before': { backgroundColor: headerBg },
        '&[data-pinned="true"]': { backgroundColor: headerBg },
        '& .Mui-TableHeadCell-Content-Wrapper': {
          whiteSpace: 'pre-wrap',
          lineHeight: 1.45,
          overflow: 'visible',
        },
      },
      size: 'small',
    }),
    isMultiSortEvent: () => true,
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.paper,
      draggingBorderColor: theme.palette.secondary.main,
    }),
    onSortingChange: (sortingUpdater: any) => {
      const isArray = typeof sortingUpdater === 'object' && Array.isArray(sortingUpdater);

      const newSortVal = isArray ? [] : sortingUpdater(sorting);
      setSort(newSortVal);
      if (onSortingChange) onSortingChange(newSortVal);
    },
    ...rest,
  });

  const count = table.getRowCount();

  return (
    <Box
      className={'flex flex-col overflow-hidden flex-1'}
      {...boxProps}
      sx={[evenColumnSx, boxProps?.sx] as BoxProps['sx']}
    >
      {customFilterUi}
      <Table table={table} />
      {paginationProps && <NewPagination {...paginationProps} count={count} />}
    </Box>
  );
};

export default NewMuiTable;
